import { useEffect, useMemo } from 'react'

import type { Message, MessageSort, PpvFollowUpMessageResponseDto } from '@api'

import {
  useCreateFollowUpMessage,
  useDeleteFollowUpMessage,
  useUpdateFollowUpMessage,
  useUpdateFollowUpMessageDelay,
  useUpdateFollowUpMessageNotSeenToggle,
  useUpdateFollowUpMessageToggle,
  useUpdateSendPurchasedPPVFollowUps,
} from '@/modules/creator-settings/follow-up-messages/query/follow-up-message-query'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { MessageSchema } from '@/modules/message/service/message-service'
import { mutate } from '@/utils/mutate'

function mapSequencesToMessages(sequences: PpvFollowUpMessageResponseDto[]) {
  try {
    const messages =
      sequences.flatMap(
        (group) =>
          group.sequence?.messages?.map(
            (message): Message => ({
              ...MessageSchema.parse(message),
              // @ts-ignore
              sequenceId: group.id,
              totalPPVBought: group?.totalPPVBought ?? 0,
              totalSent: group?.totalSent ?? 0,
              totalReads: group?.totalReads ?? 0,
              totalResponses: group?.totalResponses ?? 0,
            }),
          ) ?? [],
      ) ?? []

    return { status: 'ok', messages }
  } catch (error) {
    return { status: 'error', error }
  }
}

export function useFollowUpMessageSettings(sortOption: MessageSort) {
  const { setSortValue } = useSettingsContext()
  const {
    isFetching,
    ppvFollowUpMessages,
    ppvFollowUpsEnabled,
    ppvFollowUpsNotSeen,
    sendPpvFollowUpsAfterMinutes,
    ppvFollowUpsBoughtNotReplied,
  } = useSettingsContext()

  useEffect(() => {
    setSortValue({
      ppvFollowUpMessageSortBy: sortOption,
    })
  }, [setSortValue, sortOption])

  const changeEnabled = useUpdateFollowUpMessageToggle()
  const changeNotSeenEnabled = useUpdateFollowUpMessageNotSeenToggle()
  const changeDelay = useUpdateFollowUpMessageDelay()
  const changeMessage = useUpdateFollowUpMessage()
  const deleteMessage = useDeleteFollowUpMessage()
  const createMessage = useCreateFollowUpMessage()
  const changeSendPurchasedPPVFollowUps = useUpdateSendPurchasedPPVFollowUps()

  return useMemo(
    () => {
      const messages = mapSequencesToMessages(ppvFollowUpMessages)

      if (messages.status === 'error') {
        console.error('Failed to load follow up messages', messages.error)
      }

      return {
        isUpdating: changeDelay.isLoading,
        isFetching,
        sendPurchasedPPVFollowUps: ppvFollowUpsBoughtNotReplied,
        enabled: ppvFollowUpsEnabled!,
        changeEnabled: mutate(changeEnabled),
        notSeenEnabled: ppvFollowUpsNotSeen,
        changeNotSeenEnabled: mutate(changeNotSeenEnabled),
        delay: sendPpvFollowUpsAfterMinutes!,
        changeDelay: mutate(changeDelay),
        messages: messages.messages ?? [],
        changeMessage: mutate(changeMessage),
        deleteMessage: mutate(deleteMessage),
        createMessage: mutate(createMessage),
        changeSendPurchasedPPVFollowUps: mutate(
          changeSendPurchasedPPVFollowUps,
        ),
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isFetching,
      ppvFollowUpsEnabled,
      changeEnabled,
      ppvFollowUpsNotSeen,
      changeNotSeenEnabled,
      sendPpvFollowUpsAfterMinutes,
      changeDelay,
      ppvFollowUpMessages,
      changeMessage,
      deleteMessage,
      createMessage,
      changeSendPurchasedPPVFollowUps,
    ],
  )
}
